import { BusinessObjectBase } from './base';
import { resourceSetSchema } from '@schemas/business-objects/resource-set';
import type { ActivityItem } from './activity-item';
import type { ResourceCategory } from './resource-category';
import type { User } from './user';
import type { Id } from '../base-types';
import type { ExportImportConfig } from '../interface-definitions/index';
import type { Resource } from './resource';
import type { TextBox } from './text-box';

export interface ResourceSetData {
  readonly id: Id;
  readonly name?: string;
  readonly resourceCategories?: ResourceCategory[];
  readonly activityItems?: ActivityItem[];
  readonly createdById: Id;
  readonly createdBy?: User;
  readonly publishedById: Id;
  readonly publishedBy?: User;
  readonly archivedById: Id;
  readonly archivedBy?: User;
  readonly locked?: boolean;
  readonly uuid?: string;
  readonly version?: number;
  readonly status?: string;
  readonly createdAt?: Date;
  readonly publishedAt?: Date;
  readonly archivedAt?: Date;
}

export interface ResourceSet extends Readonly<ResourceSetData> {}
type ResourceSetCreateWithChildrenData = Partial<
  Omit<ResourceSetData, 'resourceCategories'>
> & { resourceCategories?: Partial<ResourceCategory>[] };

export class ResourceSet extends BusinessObjectBase {
  static readonly entityName = 'ResourceSet' as const;
  static readonly plural = 'resource-sets';
  static schema = resourceSetSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ResourceSet[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ResourceSet(data));
  }

  static async findOne(filter?: any): Promise<ResourceSet | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ResourceSet(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ResourceSet> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ResourceSet(data);
  }

  static async create(data: Omit<ResourceSetData, 'id'>): Promise<ResourceSet> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ResourceSet(result);
  }

  static async createMany(
    data: Omit<ResourceSetData, 'id'>[],
  ): Promise<ResourceSet[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ResourceSet(item));
  }

  static async createWithChildren(
    data: ResourceSetCreateWithChildrenData,
  ): Promise<ResourceSet> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ResourceSet(result);
  }

  static async createManyWithChildren(
    data: ResourceSetCreateWithChildrenData[],
  ): Promise<ResourceSet[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ResourceSet(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ResourceSetData>,
  ): Promise<ResourceSet> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ResourceSet(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async export(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSet.plural}/${(this as any).id}/export`;
    return BusinessObjectBase.get<any>(url);
  }
  static async exportMany(body: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/export-many`;
    return this.post<any>(url, body);
  }
  async publish(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSet.plural}/${(this as any).id}/publish`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async newVersion(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSet.plural}/${(this as any).id}/new-version`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async latestVersion(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSet.plural}/${(this as any).id}/latest-version`;
    return BusinessObjectBase.get<any>(url);
  }
  static async findLatestVersions(
    filter: any,
    publishedOnly?: boolean,
  ): Promise<any[]> {
    const url = `${this.baseUrl}/${this.plural}/find-latest-versions`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined && filter !== null)
      queryParams.set('filter', JSON.stringify(filter));
    if (publishedOnly !== undefined && publishedOnly !== null)
      queryParams.set('publishedOnly', String(publishedOnly));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<any[]>(fullUrl);
  }
  async archive(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSet.plural}/${(this as any).id}/archive`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceSet.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
